/**
 * Cursor CLI ACP client: JSON-RPC 2.0 over stdio, matching Cursor's protocol.
 * Implements ACP client filesystem methods so we can snapshot before/after writes and
 * report unified diffs to the session (see Agent Client Protocol tool-calls + file-system).
 *
 * **Separate from `sdk/sdk-stdio-acp-client.ts`.** Cursor does not use
 * `@agentclientprotocol/sdk` `ClientSideConnection`; handshake and method names differ.
 * Session restore uses the same ACP flow as stdio agents: `initialize` capabilities → prefer
 * `session/resume`, then `session/load` (replay suppressed for the bridge), then `session/new`.
 * Bridge hooks assume `onSessionUpdate` receives the **inner** `update` object with
 * `sessionUpdate` at top level — keep that contract when changing this file.
 *
 * @see https://cursor.com/docs/cli/acp
 * @see https://agentclientprotocol.com/protocol/tool-calls
 * @see https://agentclientprotocol.com/protocol/file-system
 */

import { getBridgeRoot } from '../../../../files/cwd/index.js';
import {
  formatJsonRpcStyleError,
  mergeErrorWithStderr,
} from '../agent-stderr-capture.js';
import type { AcpClientHandle, AcpClientOptions } from '../acp-client.js';
import {
  bridgeInstalledAgentAuthProcessEnv,
  cursorAgentUsesApiKeyAuth,
} from '../bridge-installed-agent-auth-env.js';
import { formatSpawnError } from '../format-spawn-error.js';
import { killChildProcessTree } from '../kill-process-tree.js';
import { buildCursorAcpSpawnCommand } from './cursor-spawn-command.js';
import { createCursorAcpSessionContext } from './create-cursor-acp-session-context.js';
import { createCursorAcpHandle } from './create-cursor-acp-handle.js';
import { initCursorAcpWire } from './cursor-acp-init.js';
import { spawnCursorAcpProcess } from './spawn-cursor-acp-process.js';

export { buildCursorAcpSpawnCommand } from './cursor-spawn-command.js';
export { BACKEND_LOCAL_AGENT_TYPE, detectLocalAgentPresence } from './cursor-local-agent.js';

export async function createCursorAcpClient(options: AcpClientOptions): Promise<AcpClientHandle> {
  const command = buildCursorAcpSpawnCommand(options.command, options.sessionMode);
  const {
    cwd = getBridgeRoot(),
    backendAgentType,
    onSessionUpdate,
    onRequest,
    onFileChange,
    persistedAcpSessionId,
    onAcpSessionEstablished,
    onAcpConfigOptionsUpdated,
    onAgentSubprocessExit,
  } = options;
  const dbgFs = process.env.BUILDAUTOMATON_DEBUG_ACP_FS === '1';

  const spawnEnv = bridgeInstalledAgentAuthProcessEnv(process.env);
  const { child, stderrCapture } = spawnCursorAcpProcess({
    command,
    cwd,
    onAgentSubprocessExit,
  });
  const skipBrowserAuthenticate = cursorAgentUsesApiKeyAuth(spawnEnv);

  const sessionCtx = createCursorAcpSessionContext({
    cwd,
    mcpServers: options.mcpServers,
    persistedAcpSessionId,
    backendAgentType,
    agentConfig: options.agentConfig,
    getActiveConfigOptions: options.getActiveConfigOptions,
    onAcpSessionEstablished,
    onAcpConfigOptionsUpdated,
    onFileChange,
    stderrCapture,
  });

  return new Promise<AcpClientHandle>((resolve, reject) => {
    child.on('error', (err: NodeJS.ErrnoException) => {
      killChildProcessTree(child, 'SIGKILL');
      reject(new Error(formatSpawnError(err, command[0])));
    });

    void (async () => {
      try {
        const { wire, transport, established, incoming, pendingRequests } = await initCursorAcpWire({
          child,
          sessionCtx,
          skipBrowserAuthenticate,
          incomingDeps: { dbgFs, sessionCtx, onSessionUpdate, onRequest },
        });

        resolve(
          createCursorAcpHandle({
            child,
            sessionId: established.sessionId,
            sessionCtx,
            transport,
            wire,
            incoming,
            pendingRequests,
          }),
        );
      } catch (err) {
        killChildProcessTree(child, 'SIGKILL');
        const merged = mergeErrorWithStderr(formatJsonRpcStyleError(err), stderrCapture.getText());
        reject(merged ? new Error(merged) : err instanceof Error ? err : new Error(String(err)));
      }
    })();
  });
}
